import React, { createContext, useContext, useEffect, useState } from 'react'
import { Project } from '../types/types'

interface ConfigData {
    header: {
        title: string
    }
    tabs: {
        home: string
        projects: string
        about: string
        contact: string
    }
    home: {
        sections: Array<{
            title: string
            description: string
            imagePlaceholder: string
        }>
    }
    about: {
        title: string
        work: {
            title: string
            content: string
        }
        life: {
            title: string
            content: string
        }
    }
    projects: {
        title: string
    }
    contact: {
        title: string
        email: {
            label: string
            value: string
            href: string
        }
        linkedin: {
            label: string
            value: string
            href: string
        }
        github: {
            label: string
            value: string
            href: string
        }
    }
    footer: {
        name: string
        email: string
        linkedin: {
            href: string
        }
        github: {
            href: string
        }
    }
    projectsData: Project[]
}

interface ConfigContextType {
    config: ConfigData | null
    loading: boolean
    error: Error | null
}

const ConfigContext = createContext<ConfigContextType>({
    config: null,
    loading: true,
    error: null,
})

export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [config, setConfig] = useState<ConfigData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)

    // Fetch config once on mount and store in state.
    // After initial load, all components access config from memory (via context),
    // making it as performant as hardcoded values.
    useEffect(() => {
        const loadConfig = async () => {
            try {
                // Fetch both config files in parallel for better performance
                const [configResponse, projectsResponse] = await Promise.all([
                    fetch('/config.json'),
                    fetch('/projectdata.json'),
                ])

                if (!configResponse.ok) {
                    throw new Error('Failed to load config')
                }
                if (!projectsResponse.ok) {
                    throw new Error('Failed to load projects data')
                }

                const [configData, projectsData] = await Promise.all([
                    configResponse.json(),
                    projectsResponse.json(),
                ])

                setConfig({
                    ...configData,
                    projectsData: projectsData as Project[],
                })
                setError(null)
            } catch (err) {
                setError(
                    err instanceof Error ? err : new Error('Unknown error')
                )
            } finally {
                setLoading(false)
            }
        }

        loadConfig()
    }, [])

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = React.useMemo(
        () => ({ config, loading, error }),
        [config, loading, error]
    )

    return (
        <ConfigContext.Provider value={contextValue}>
            {children}
        </ConfigContext.Provider>
    )
}

export const useConfig = () => {
    const context = useContext(ConfigContext)
    if (!context) {
        throw new Error('useConfig must be used within a ConfigProvider')
    }
    return context
}
